'use client';

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaSearchPlus, FaTimes } from 'react-icons/fa';
import { uploadsImageLoader } from '@/lib/imageLoader';

// Responsive variants (-w320/-w640/-w1080.webp) exist for every /uploads/
// image; a phone needs the 640w one (~27 KB), not the 1920w original (~90 KB).
function variantSrcSet(src: string | undefined, widths: number[]): string | undefined {
  if (!src || !src.startsWith('/uploads/')) return undefined;
  return widths.map(w => `${uploadsImageLoader({ src, width: w })} ${w}w`).join(', ');
}

// A missing variant must never leave a blank tile: drop srcset and retry the original.
function fallBackToOriginal(e: React.SyntheticEvent<HTMLImageElement>) {
  const img = e.currentTarget;
  if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
}

interface ProductImageGalleryProps {
  images: Array<{ image_url: string; alt_text?: string }>;
  productName: string;
}

export default function ProductImageGallery({ images, productName }: ProductImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const mainImageRef = useRef<HTMLDivElement>(null);

  const currentImage = images[selectedIndex]?.image_url;

  useEffect(() => { setMounted(true); }, []);

  // Close on Escape
  useEffect(() => {
    if (!zoomOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomOpen(false); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [zoomOpen]);

  if (!images.length) return null;

  const zoomModal = zoomOpen && mounted ? createPortal(
    <div
      className="fixed inset-0 bg-black/95 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={() => setZoomOpen(false)}
    >
      <button
        className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-3"
        onClick={() => setZoomOpen(false)}
      >
        <FaTimes className="w-6 h-6" />
      </button>
      {images.length > 1 && (
        <>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3"
            onClick={(e) => { e.stopPropagation(); setSelectedIndex((prev) => (prev - 1 + images.length) % images.length); }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3"
            onClick={(e) => { e.stopPropagation(); setSelectedIndex((prev) => (prev + 1) % images.length); }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </>
      )}
      <img
        src={currentImage}
        alt={productName}
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {images.length > 1 && (
        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
          {selectedIndex + 1} / {images.length}
        </p>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* Main Image */}
      <div
        ref={mainImageRef}
        className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4 relative cursor-zoom-in group"
        onClick={() => setZoomOpen(true)}
      >
        <img
          src={currentImage}
          srcSet={variantSrcSet(currentImage, [640, 1080, 1920])}
          sizes="(min-width: 1024px) 50vw, 100vw"
          fetchPriority={selectedIndex === 0 ? 'high' : undefined}
          onError={fallBackToOriginal}
          alt={productName}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
            <FaSearchPlus className="w-5 h-5 text-gray-700" />
          </div>
        </div>
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {images.slice(0, 8).map((img, i) => (
            <button
              key={i}
              onClick={() => setSelectedIndex(i)}
              className={`aspect-square bg-gray-100 rounded overflow-hidden border-2 transition-colors ${
                i === selectedIndex ? 'border-primary-red' : 'border-transparent hover:border-gray-300'
              }`}
            >
              <img
                src={img.image_url}
                srcSet={variantSrcSet(img.image_url, [320, 640])}
                sizes="(min-width: 1024px) 12vw, 25vw"
                loading="lazy"
                onError={fallBackToOriginal}
                alt={img.alt_text || `${productName} view ${i + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {zoomModal}
    </>
  );
}
